import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { productColumns } from './dashboard-columns';
import { PRODUCT_PAGE_SIZE, useTopProductsPage } from './dashboard.api';
import type { DashboardQuery } from './dashboard.types';
import { SortableTable } from '@/components/ui/sortable-table';

/**
 * The detail behind "Top products".
 *
 * Every other breakdown on the dashboard details a slice of what the page has
 * already fetched, so its search filters rows in the browser. That is wrong
 * here: the summary asks for the top twenty-five items, and searching those
 * answers "is this product in the top twenty-five" when the question being
 * asked is "how did this product do". So this list pages and searches against
 * `trends/items` instead, and a search reaches the whole catalogue.
 *
 * The term is committed on submit rather than per keystroke — each one is a
 * query over the period's order lines, and the endpoint is not cheap.
 */
export function TopProductsDetail({ query }: { query: DashboardQuery }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);

  // A new term is a new result set; staying on page 4 would land on an empty
  // one. Same for a range or store change made behind the open sheet.
  useEffect(() => setPage(0), [term, query]);

  const { data, isPending, isFetching } = useTopProductsPage(query, page, term);

  return (
    <div className="space-y-3">
      <form
        role="search"
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setTerm(draft);
        }}
      >
        <div className="relative flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('dashboard.searchProducts')}
            aria-label={t('dashboard.searchProducts')}
            className="pl-9"
          />
        </div>
        <Button type="submit" disabled={isFetching}>
          <Search className="sm:hidden" />
          <span className="hidden sm:inline">{t('common.search')}</span>
        </Button>
      </form>

      {term && (
        <p className="text-muted-foreground text-xs">
          {t('dashboard.searchingAll')}
        </p>
      )}

      <SortableTable
        rows={data?.rows ?? []}
        columns={productColumns(t)}
        rowKey={(row) => row.id}
        loading={isPending}
        busy={isFetching && !isPending}
        pagination={{
          page,
          size: PRODUCT_PAGE_SIZE,
          total: data?.total ?? 0,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
